import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, VolumeX, Play, Maximize2 } from "lucide-react";
import {
  fetchTutorial, isTutorialAvailable, markTutorialSeen,
  optOutAllTutorials, getTitle,
} from "@/lib/tutorials";

/**
 * TutorialWelcomeModal
 * --------------------
 * Big centered welcome modal that opens on first visit to /sandbox.
 * NON-BLOCKING: the prospect can dismiss and start clicking the deck
 * immediately. Backdrop click also dismisses. Only shows if:
 *   - a videoUrl is uploaded for the key
 *   - the user hasn't already seen it
 *   - the user hasn't opted out of tutorials globally
 */
export const TutorialWelcomeModal = ({
  tutorialKey = "sandbox.overview",
  autoOpenDelayMs = 500,
  waitForKey = null,   // If set, this modal defers to `waitForKey` — it won't
                       // open on this visit while `waitForKey` still has an
                       // unwatched video available. Prevents two auto-play
                       // welcome videos from firing at the same time.
}) => {
  const [entry, setEntry] = useState(null);
  const [open, setOpen]   = useState(false);
  const [pip, setPip]     = useState(false);      // Floating picture-in-picture mode
  const [muted, setMuted] = useState(true);
  const [dontShow, setShow] = useState(false);
  const videoRef = useRef(null);
  const pipVideoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Defer to a higher-priority key if one is still pending (e.g. main
      // welcome should play before per-industry overviews).
      if (waitForKey && await isTutorialAvailable(waitForKey)) return;
      if (!(await isTutorialAvailable(tutorialKey))) return;
      const t = await fetchTutorial(tutorialKey);
      if (cancelled || !t) return;
      setEntry(t);
      setTimeout(() => { if (!cancelled) setOpen(true); }, autoOpenDelayMs);
    })();
    return () => { cancelled = true; };
  }, [tutorialKey, autoOpenDelayMs, waitForKey]);

  // When the panel closes into PIP, keep the media playing seamlessly.
  useEffect(() => {
    if (pip && pipVideoRef.current) {
      pipVideoRef.current.play().catch(() => {});
    }
  }, [pip]);

  const startDemo = () => {
    // Do NOT kill the video — collapse it into a floating PIP so the
    // presenter keeps talking while the prospect explores the deck.
    markTutorialSeen(tutorialKey);
    if (dontShow) optOutAllTutorials();
    setOpen(false);
    setPip(true);
  };

  const dismissFully = () => {
    markTutorialSeen(tutorialKey);
    if (dontShow) optOutAllTutorials();
    setOpen(false);
    setPip(false);
  };

  if (!entry) return null;

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            {/* NO fullscreen backdrop — the deck stays fully clickable behind
                the corner modal. Only the modal itself captures clicks. */}
            <motion.div
              initial={{ opacity: 0, x: 40, scale: 0.96 }}
              animate={{ opacity: 1, x: 0,  scale: 1 }}
              exit={{ opacity: 0, x: 40, scale: 0.96 }}
              transition={{ type: "spring", damping: 24, stiffness: 260 }}
              className="fixed bottom-6 right-6 z-[80] w-full max-w-3xl md:max-w-4xl"
              data-testid="welcome-modal"
            >
              <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-900/10 overflow-hidden">
                <div className="relative bg-slate-900 aspect-video">
                  <video
                    ref={videoRef}
                    src={entry.url}
                    poster="/nex-mark.png"
                    autoPlay muted={muted} playsInline
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <button
                    onClick={() => setMuted(m => !m)}
                    data-testid="welcome-mute"
                    className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/70 hover:bg-black/90 text-white transition-colors"
                    aria-label={muted ? "Unmute" : "Mute"}
                  >
                    {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                    <span className="text-[10px] uppercase font-bold tracking-widest">
                      {muted ? "Tap for sound" : "Sound on"}
                    </span>
                  </button>
                  <button
                    onClick={dismissFully}
                    data-testid="welcome-close"
                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
                    aria-label="Close welcome"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-indigo-700">
                    Welcome to NEX
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mt-1 leading-tight">
                    {entry.title || getTitle(tutorialKey)}
                  </h2>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                    Click any card on the deck to see it in action — the presenter keeps talking in the corner.
                  </p>
                  <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-[12px] text-slate-500 cursor-pointer select-none">
                      <input type="checkbox" checked={dontShow} onChange={e => setShow(e.target.checked)}
                             data-testid="welcome-optout" className="accent-indigo-600" />
                      Don&apos;t show again
                    </label>
                    <button
                      onClick={startDemo}
                      data-testid="welcome-start"
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-cyan-500 text-white text-[12px] font-bold uppercase tracking-widest hover:shadow-lg transition-all flex items-center gap-2"
                    >
                      <Play size={13} /> Start the demo
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Floating picture-in-picture: video keeps playing in a bottom corner
          while the prospect interacts with the deck. Click to re-expand. */}
      <AnimatePresence>
        {pip && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0,  scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            className="fixed bottom-4 right-4 z-[70] w-96 rounded-xl overflow-hidden shadow-2xl ring-1 ring-slate-900/20 bg-slate-900"
            data-testid="welcome-pip"
          >
            <div className="relative aspect-video">
              <video
                ref={pipVideoRef}
                src={entry.url}
                poster="/nex-mark.png"
                autoPlay muted={muted} playsInline loop
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <button
                onClick={() => setMuted(m => !m)}
                data-testid="pip-mute"
                className="absolute bottom-1.5 left-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted ? <VolumeX size={11} /> : <Volume2 size={11} />}
              </button>
              <button
                onClick={() => { setPip(false); setOpen(true); }}
                data-testid="pip-expand"
                className="absolute bottom-1.5 left-10 w-7 h-7 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
                aria-label="Expand"
              >
                <Maximize2 size={11} />
              </button>
              <button
                onClick={dismissFully}
                data-testid="pip-close"
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
                aria-label="Close"
              >
                <X size={11} />
              </button>
              <div className="absolute top-1.5 left-1.5 text-[8px] font-bold uppercase tracking-widest text-white/95 bg-indigo-600 px-1.5 py-0.5 rounded">
                Live tour
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/**
 * TutorialInline
 * --------------
 * Compact video block rendered INSIDE the drawer, above the Score 10 card.
 * Auto-plays muted the moment the drawer opens. Users can unmute or fully
 * collapse it. Silent no-op if no clip is uploaded for the key.
 */
export const TutorialInline = ({ tutorialKey }) => {
  const [entry, setEntry] = useState(null);
  const [muted, setMuted] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!(await isTutorialAvailable(tutorialKey))) return;
      const t = await fetchTutorial(tutorialKey);
      if (!cancelled && t) setEntry(t);
    })();
    return () => { cancelled = true; };
  }, [tutorialKey]);

  if (!entry) return null;
  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        data-testid="inline-expand"
        className="w-full mb-4 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[11px] font-bold uppercase tracking-widest transition-colors"
      >
        <Play size={12} /> Replay tour
      </button>
    );
  }

  const onDismiss = () => {
    markTutorialSeen(tutorialKey);
    setCollapsed(true);
  };

  return (
    <div className="mb-4 rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-testid="inline-tutorial">
      <div className="relative bg-slate-900 aspect-video">
        <video
          src={entry.url}
          poster="/nex-mark.png"
          autoPlay muted={muted} playsInline loop
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <button
          onClick={() => setMuted(m => !m)}
          data-testid="inline-mute"
          className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
        </button>
        <button
          onClick={onDismiss}
          data-testid="inline-close"
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
          aria-label="Collapse tour"
        >
          <X size={12} />
        </button>
        <div className="absolute bottom-2 left-2 text-[9px] font-bold uppercase tracking-widest text-white/90 bg-indigo-600 px-2 py-1 rounded">
          15-sec tour
        </div>
      </div>
    </div>
  );
};

export default TutorialInline;
